import React, { useMemo, useState } from 'react';
import { DataTable } from '@/components/common/data-table/data-table';
import { useDataTable } from '@/components/common/data-table/use-data-table';
import type { ColumnDef } from '@/lib/tanstack-table';
import { LineItem } from 'src/types/application/SalesOrderDraftDTO';
import AssociateProductV2 from '@/components/products/AssociateProductV2';
import {
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CheckCircleIcon, WarningIcon } from '@phosphor-icons/react';
import useCustomerProduct from '@/api/inventory-management/products/useCustomerProduct';
import { useGT } from 'gt-react';

function SalesOrderDraftAssociateLineItemPopoverComponent({
  lineItem,
  customerId,
  tooltipText,
  onVariantSelected,
}: {
  lineItem: LineItem;
  customerId?: string;
  tooltipText?: string;
  onVariantSelected: ({
    productId,
    productVariantId,
  }: {
    productId?: string;
    productVariantId?: string;
  }) => void;
}) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const tableData = useMemo(() => [lineItem], [lineItem]);

  const associateColumns: ColumnDef<LineItem, unknown>[] = useMemo(
    () => [
      {
        header: gt('ID', {
          context: 'sales order associate line item table column',
        }),
        accessorKey: 'customerIdentifier',
        cell: ({ row }) => {
          const item = row.original as LineItem;
          const identifier = item.customerIdentifier || item.sku || item.upc;
          return (
            <span className="text-xs text-muted-foreground">
              {identifier || '-'}
            </span>
          );
        },
      },
      {
        header: gt('Item', {
          context: 'sales order associate line item table column',
        }),
        accessorKey: 'customerDescription',
        cell: ({ row }) => {
          const item = row.original as LineItem;
          const description =
            item.customerDescription ||
            item.itemName ||
            item.name ||
            gt('No description available', {
              context: 'placeholder when line item has no description',
            });
          return (
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">
                {description}
              </span>
            </div>
          );
        },
      },
      {
        header: gt('Unit Price', {
          context: 'sales order associate line item table column',
        }),
        accessorKey: 'unitPrice',
        cell: ({ getValue }) => {
          const val = getValue() as number | undefined;
          return (
            <span className="text-xs">
              {val ? `$${Number(val).toFixed(2)}` : ''}
            </span>
          );
        },
      },
    ],
    [gt],
  );

  const table = useDataTable<LineItem>({
    columns: associateColumns,
    data: tableData,
    options: { initialState: { pagination: { pageIndex: 0, pageSize: 5 } } },
  });

  // Resolve a product/variant from CustomerProduct when present
  const currentCustomerProductId =
    lineItem.selectedCustomerProductId || lineItem.suggestedCustomerProductId;
  const { data: currentCustomerProduct } = useCustomerProduct(
    currentCustomerProductId,
    !!currentCustomerProductId,
  );

  const selectedProductVariantId =
    lineItem.selectedProductVariantId ||
    lineItem.suggestedProductVariantId ||
    currentCustomerProduct?.customerProduct.productVariantId;

  const selectedProductId =
    lineItem.suggestedProductId ||
    currentCustomerProduct?.customerProduct.productId;
  const hasCustomerProduct = !!lineItem.selectedCustomerProductId;
  const hasProductVariant = !!lineItem.selectedProductVariantId;

  return (
    <Popover modal open={open} onOpenChange={(next) => setOpen(next)}>
      {/* Tooltip and Popover triggers must `asChild` onto the SAME leaf
          element, with the Popover as the outer provider. Nesting a Popover
          inside a separate TooltipTrigger subtree collapses the triggers and
          the popover stops opening on click. */}
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <div className="flex items-center justify-center w-full h-full cursor-pointer rounded p-1 hover:bg-muted">
              {hasCustomerProduct || hasProductVariant ? (
                <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
              ) : (
                <WarningIcon className="h-4 w-4 text-amber-500 flex-shrink-0" />
              )}
            </div>
          </PopoverTrigger>
        </TooltipTrigger>
        {tooltipText ? (
          <TooltipContent>
            <span className="text-xs">{tooltipText}</span>
          </TooltipContent>
        ) : null}
      </Tooltip>
      <PopoverContent
        side="right"
        className="w-[800px] max-h-[70vh] overflow-y-auto"
      >
        <PopoverArrow />
        <AssociateProductV2
          selectedProductVariantId={
            selectedProductVariantId ?? lineItem.suggestedProductVariantId
          }
          selectedProductId={selectedProductId ?? lineItem.suggestedProductId}
          topProductVariantMatches={lineItem.topProductVariantMatches}
          customerId={customerId}
          onVariantSelected={({ productId, productVariantId }) => {
            onVariantSelected({ productId, productVariantId });
            setOpen(false);
          }}
        >
          {/* Sticky Customer Request Section */}
          <div className="sticky top-0 z-10 bg-background border-b border-border pb-2 mb-2">
            <p className="text-sm font-medium mb-1">
              {gt('Customer Request', {
                context:
                  'heading for original customer line item in associate popover',
              })}
            </p>
            <div className="p-2 border border-gray-300 rounded">
              <DataTable table={table} />
            </div>
          </div>
        </AssociateProductV2>
      </PopoverContent>
    </Popover>
  );
}

export const SalesOrderDraftAssociateLineItemPopover = React.memo(
  SalesOrderDraftAssociateLineItemPopoverComponent,
);
